import { useEffect, useMemo } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import { useTripFilterContext } from '@vakantiesnl/services/src/context/useTripFilterContext';
import { useGetAccommodationByEntityId } from '@vakantiesnl/services/src/hooks/queries/useGetAccommodationByEntityId';
import { getDepartureDateAndOffset, PAGE_OFFSET } from '@vakantiesnl/services/src/hooks/utils/offerUtils';
import { type OfferResponse } from '@vakantiesnl/services/src/search';
import { mapCompactAccommodations } from '@vakantiesnl/services/src/search/vaknl-mapper';
import { useAccoOfferStore } from '@vakantiesnl/services/src/stores/accommodationStore/useAccoStore';
import { usePartyComposition } from '@vakantiesnl/services/src/stores/globalFiltersStore/globalFiltersHooks';
import { isPackageOffer } from '@vakantiesnl/services/src/util';
import { isNotEmpty } from '@vakantiesnl/services/src/util/arrayUtils';
import {
	getDateWithoutTime,
	getDateYMD,
	getMaxDepartureDate,
	getMinDepartureDate,
} from '@vakantiesnl/services/src/util/dateUtils';
import { fetchHandler } from '@vakantiesnl/services/src/util/fetchHandler';
import { getDefaultDate } from '@vakantiesnl/services/src/util/getDefaultDate';
import { fetchVakNL } from '@vakantiesnl/services/src/util/vaknl-fetch';
import type { Search } from '@vakantiesnl/types/src';

import type { DepartureDateOffersRequestBody } from './types';
import { findDurationConfigs, mapMatrixOffers } from './utils';

type Filters = {
	party: Search.PartyComposition[];
	durations: number[];
	airports: string[];
	mealplans: string[];
	departureDate: string | undefined;
	hasUserSelectedDate: boolean; // Did the user manually select the departure date or not
	withSelectableTransfer: boolean;
};

export type GetMatrixDepartureDateOffersQuery = ReturnType<typeof useGetDepartureDatePackageMatrixOffers>;
export type MatrixOffers = Record<string, Array<Search.OfferItem | null>>;
export type DepartureDateOffersMatrixResponse = {
	min: number;
	max: number;
	cheapest: string;
	cheapestOffer: Search.OfferItem | undefined;
	cheapestPricePerAdult: number;
	all: Search.OfferItem[];
	party: Search.RawParty;
	matrixOffers: MatrixOffers | undefined;
};

export function useGetOfferFilters(): Filters {
	const {
		state,
		computed: { durations },
	} = useTripFilterContext();
	const party = usePartyComposition();

	return useMemo(() => {
		return {
			party,
			durations: durations.map((value) => value - 1),
			airports: state.airports || [],
			mealplans: state.mealplans || [],
			departureDate: state.departureDate?.[0] || getDefaultDate(),
			hasUserSelectedDate: !!state.departureDate?.length || !!state.selected.date,
			withSelectableTransfer: !!state.withSelectableTransfer,
		};
	}, [
		party,
		durations,
		state.airports,
		state.mealplans,
		state.departureDate,
		state.selected.date,
		state.withSelectableTransfer,
	]);
}

const safeMin = (arr: number[] | undefined): number => (arr?.length ? Math.min(...arr) : 0);
const safeMax = (arr: number[] | undefined): number => (arr?.length ? Math.max(...arr) : 0);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useGetDepartureDatePackageMatrixOffers() {
	const { state } = useTripFilterContext();

	const { accommodation } = useGetAccommodationByEntityId();
	const giataId = accommodation.giataId;

	const { hasUserSelectedDate, ...filters } = useGetOfferFilters();

	const isDepartureDateLoading = useAccoOfferStore((s) => s.isDepartureDateLoading);
	const setIsDepartureDateLoading = useAccoOfferStore((s) => s.setIsDepartureDateLoading);
	const setTotalDepartureDateOffers = useAccoOfferStore((s) => s.setTotalDepartureDateOffers);

	const dateToStartFrom = state.selected.date || filters.departureDate || getDateYMD(getMinDepartureDate());

	/** Convert durations (nights) to duration object config to fetch a duration range based on the duration passed  */
	const durationArrayDays = findDurationConfigs(filters.durations.map((value) => value + 1)).sort((a, b) => a - b);
	const durationArrayNights = durationArrayDays.map((d) => d - 1);

	const infiniteQuery = useInfiniteQuery({
		queryKey: ['departureDateMatrixInfiniteScroll', giataId, JSON.stringify(filters)],
		queryFn: ({ pageParam }) => fetchDepartureDateMatrixOffers(giataId, filters, pageParam, durationArrayNights),
		enabled: accommodation.isBookable,
		getNextPageParam: (_lastPage, _allPages, lastStartDate) => {
			const maxDepartureDate = getMaxDepartureDate();

			if (!lastStartDate || getDateWithoutTime(lastStartDate).isSameOrAfter(maxDepartureDate)) {
				return undefined;
			}

			const nextStartDate = getDateWithoutTime(lastStartDate).add(PAGE_OFFSET, 'day');

			return getDateYMD(nextStartDate);
		},
		getPreviousPageParam: (_lastPage, _allPages, lastStartDate) => {
			const minDepartureDate = getMinDepartureDate();

			if (!lastStartDate || getDateWithoutTime(lastStartDate).isSameOrBefore(minDepartureDate)) {
				return undefined;
			}

			const nextStartDate = getDateWithoutTime(lastStartDate).subtract(PAGE_OFFSET, 'day');

			return getDateYMD(nextStartDate);
		},
		initialPageParam: dateToStartFrom
			? getDateYMD(getDateWithoutTime(dateToStartFrom).subtract(15, 'day'))
			: undefined,
	});

	const mergedOffers = useMemo(() => {
		return infiniteQuery.data?.pages ? getMergedDepartureDateMatrixOffers(infiniteQuery.data.pages) : undefined;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [infiniteQuery.data?.pages]);

	useEffect(() => {
		const total = Object.keys(mergedOffers?.matrixOffers || {}).length;
		setTotalDepartureDateOffers(total || 0);
	}, [mergedOffers, setTotalDepartureDateOffers]);

	useEffect(() => {
		if (infiniteQuery.isLoading !== isDepartureDateLoading) {
			setIsDepartureDateLoading(infiniteQuery.isLoading);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [infiniteQuery.isLoading]);

	return {
		departureDateOffers: mergedOffers,
		requestedDurations: durationArrayDays,
		fetchNextDepartureDateOffers: infiniteQuery.fetchNextPage,
		fetchPrevDepartureDateOffers: infiniteQuery.fetchPreviousPage,
		hasNextDepartureDateOffers: infiniteQuery.hasNextPage,
		hasPrevDepartureDateOffers: infiniteQuery.hasPreviousPage,
		isFetchingDepartureDateOffers: infiniteQuery.isFetching,
	};
}

function getMergedDepartureDateMatrixOffers(
	data: (DepartureDateOffersMatrixResponse | undefined)[],
): DepartureDateOffersMatrixResponse {
	const allOffers = data.flatMap((d) => d?.all).filter(isNotEmpty);

	const min = safeMin(
		data
			.flatMap((d) => d?.min)
			.filter((i) => i && i > 0)
			.filter(isNotEmpty),
	);
	const max = safeMax(
		data
			.flatMap((d) => d?.max)
			.filter((i) => i && i > 0)
			.filter(isNotEmpty),
	);
	const pricesPerAdult = allOffers.map(({ pricePerAdult }) => pricePerAdult);
	const cheapestPricePerAdult = safeMin(pricesPerAdult);
	const party = data.flatMap((d) => d?.party).filter(isNotEmpty)[0];
	const cheapest = allOffers.find((o) => min === o.priceTotal);
	const matrixOffers = Object.fromEntries(data.flatMap((d) => Object.entries(d?.matrixOffers || {})));

	return {
		all: allOffers,
		min,
		max,
		cheapest: cheapest?.id || '',
		cheapestOffer: cheapest,
		cheapestPricePerAdult,
		party,
		matrixOffers,
	};
}

async function fetchDepartureDateMatrixOffers(
	giataId: number | undefined,
	filters: Omit<Filters, 'hasUserSelectedDate'>,
	nextDepartureDate: string | undefined,
	durationArrayNights: number[],
): Promise<DepartureDateOffersMatrixResponse | undefined> {
	if (!giataId) {
		return undefined;
	}

	const { departureDate, preOffset, postOffset } = getDepartureDateAndOffset(nextDepartureDate);

	const requestBody: DepartureDateOffersRequestBody = {
		brand: 'ZVRNL',
		party: filters.party[0],
		rooms: filters.party,
		/** Calculate the page size to request all available offers */
		page_size: durationArrayNights.length * (preOffset + postOffset),
		variant: 'date',
		pre_offset: preOffset,
		post_offset: postOffset,
		departure_date: departureDate,
		filters: {
			/** Convert to days to nights */
			...(filters.durations.length && { durations_flight: durationArrayNights }),
			...(filters.airports.length && { departure_airports: filters.airports }),
			...(filters.mealplans.length && { mealplans: filters.mealplans }),
		},
		with_selectable_transfer: filters.withSelectableTransfer,
	};

	const result = await fetchHandler<OfferResponse>({
		fetchFn: () =>
			fetchVakNL({
				input: new Request(
					`${process.env.NEXT_PUBLIC_SEARCH_ENDPOINT_URL}/api/v1/accommodation/${giataId}/matrix-offers?check_availability=true`,
					{
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify(requestBody),
					},
				),
			}),
		errorMessage: 'Cannot fetch departure date matrix offers',
		logOnError: JSON.stringify(requestBody),
	});

	const accommodations = mapCompactAccommodations(result.accommodations);
	const allOffers = accommodations.length ? accommodations[0].offers.filter(isPackageOffer) : [];

	const mappedMatrixOffers = mapMatrixOffers(allOffers, durationArrayNights);

	const prices = allOffers.map(({ priceTotal }) => priceTotal);
	const pricesPerAdult = allOffers.map(({ pricePerAdult }) => pricePerAdult);
	const [min, max] = [safeMin(prices), safeMax(prices)];
	const cheapestPricePerAdult = safeMin(pricesPerAdult);
	const cheapest = allOffers.find(({ priceTotal }) => min === priceTotal);

	return {
		min,
		max,
		party: result.party,
		cheapest: cheapest?.id || '',
		cheapestOffer: cheapest,
		cheapestPricePerAdult,
		all: allOffers,
		matrixOffers: mappedMatrixOffers,
	};
}
